import React, {
  FunctionComponent,
  useEffect,
  useCallback,
  ChangeEvent,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import bsbApi from 'scenes/common/api/bsb';
import { LoaderModal, SlideIn, SubmitButton } from 'components';
import { TextSmall } from 'components/chroma/extended';

import {
  InputFormatQuestion,
  InputQuestion,
} from 'scenes/common/components/Questions';
import { ApiData, BsbDetails, DirectDebitDetails } from 'scenes/common/types';
import {
  hasData,
  getApiData,
  getApiErrorMessage,
  isApiError,
  makeApiSuccess,
} from 'scenes/common/helpers/apiData';
import { defaultBsbDetails } from 'scenes/common/flows/paymentDetails/defaults';

const BSB = 'bsb';
const BSB_DETAILS = 'bsb-details';
const ACCOUNT_NAME = 'accountName';
const ACCOUNT_NUMBER = 'accountNumber';
const BSB_LENGTH = 6;
const MAX_ACCOUNT_NAME_LENGTH = 30;
const VALID_ACCOUNT_NAME_PATTERN = /^[A-Za-z0-9]+[A-Za-z0-9 ]*$/;

const bsbLookupRequired = (
  bsb: string,
  bsbDetails: ApiData<BsbDetails>
): boolean =>
  bsb?.length === BSB_LENGTH &&
  ((hasData(bsbDetails) &&
    bsb !== getApiData(bsbDetails).bsb.replace('-', '')) ||
    !hasData(bsbDetails));

const isValidBsb = (
  bsb: string,
  bsbDetails: ApiData<BsbDetails | undefined>
): boolean =>
  bsb !== '' &&
  hasData(bsbDetails) &&
  bsb === getApiData(bsbDetails)?.bsb.replace('-', '');

const BsbDetailsWrapper = styled.div`
  margin-top: -0.3rem;
  margin-bottom: 1rem;
`;

const DirectDebitPaymentStep: FunctionComponent<{
  onDirectDebitPayment: (directDebitDetails: DirectDebitDetails) => void;
  buttonText?: string;
}> = ({ onDirectDebitPayment, buttonText = 'Buy now' }) => {
  const {
    formState: { isValid },
    handleSubmit,
    errors,
    control,
    getValues,
    setError,
    watch,
  } = useForm({
    mode: 'onChange',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [bsbDetailsData, setBsbDetailsData] = useState<ApiData<BsbDetails>>(
    makeApiSuccess(defaultBsbDetails)
  );

  const watchedBsb = watch(BSB);

  const onSubmit = useCallback(() => {
    onDirectDebitPayment({
      accountName: getValues(ACCOUNT_NAME),
      accountNumber: getValues(ACCOUNT_NUMBER),
      bsbDetails: getApiData(bsbDetailsData),
    });
  }, [bsbDetailsData, getValues, onDirectDebitPayment]);

  const handleBsbChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      onChange(event);
      const bsb = (event as unknown) as string;
      if (bsbLookupRequired(bsb, bsbDetailsData)) {
        setIsLoading(true);
      }
    },
    [bsbDetailsData]
  );

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      const { value } = event.target;

      if (!value || value.match(VALID_ACCOUNT_NAME_PATTERN)) {
        onChange(value);
      }
    },
    []
  );

  useEffect(() => {
    let componentMounted = true;

    if (isLoading) {
      bsbApi()
        .getBsbDetails(getValues(BSB))
        .then((bsbDetails) => {
          if (isApiError(bsbDetails)) {
            setError(BSB, {
              type: 'manual',
              message: getApiErrorMessage(bsbDetails),
            });
          }
          if (componentMounted) {
            setBsbDetailsData(
              isApiError(bsbDetails)
                ? makeApiSuccess(defaultBsbDetails)
                : bsbDetails
            );
            setIsLoading(false);
          }
        });
    }

    return () => {
      componentMounted = false;
    };
  }, [getValues, isLoading, setError]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputFormatQuestion
          size={4}
          errors={errors}
          ariaRequired
          format="### - ###"
          placeholder="e.g. 123 - 456"
          control={control}
          stateKey={BSB}
          ariaLabelledBy={
            isValidBsb(watchedBsb, bsbDetailsData) ? BSB_DETAILS : undefined
          }
          question="BSB"
          defaultValue=""
          isNumericString
          changeHandler={handleBsbChange}
          validationRules={{
            required: true,
            minLength: {
              value: BSB_LENGTH,
              message: 'Please enter a valid BSB of 6 characters',
            },
          }}
        />

        {isValidBsb(watchedBsb, bsbDetailsData) && (
          <SlideIn>
            <BsbDetailsWrapper aria-live="polite" id={BSB_DETAILS}>
              <TextSmall weight={700} margin={{ bottom: 0 }}>
                {getApiData(bsbDetailsData).bankName}
              </TextSmall>
              <TextSmall>
                {getApiData(bsbDetailsData).branchAddress},{' '}
                {getApiData(bsbDetailsData).branchPostcode}
              </TextSmall>
            </BsbDetailsWrapper>
            <InputQuestion
              errors={errors}
              control={control}
              stateKey={ACCOUNT_NAME}
              defaultValue=""
              maxLength={MAX_ACCOUNT_NAME_LENGTH}
              changeHandler={handleNameChange}
              validationRules={{ required: true }}
              question="Account name"
              ariaRequired
            />
          </SlideIn>
        )}

        {Boolean(getValues(ACCOUNT_NAME)) && (
          <SlideIn>
            <InputFormatQuestion
              size={4}
              errors={errors}
              format="#########"
              placeholder="123456"
              control={control}
              stateKey={ACCOUNT_NUMBER}
              question="Account number"
              defaultValue=""
              isNumericString
              validationRules={{ required: true }}
              ariaRequired
            />
          </SlideIn>
        )}

        <SubmitButton
          enabled={isValidBsb(watchedBsb, bsbDetailsData) && isValid}
        >
          {buttonText}
        </SubmitButton>
      </form>
      <LoaderModal isOpen={isLoading} />
    </>
  );
};

export default DirectDebitPaymentStep;
